import { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import type { GoogleListingResponse } from '../types/admin-garage-edit';
import LinkGoogleListingDialog from './LinkGoogleListingDialog';

interface GoogleListingSectionProps {
  garageId: string;
  garageName: string;
  /** Mirrors the page's `auth.hasScreen('garages')` — read-only admins see state but no actions. */
  canEdit: boolean;
}

/**
 * The Google listing block on the garage detail Information tab (Spec 058).
 *
 * Lives here rather than in its own tab because it is identity data about the garage, and this is
 * where a reviewer already looks for that. It fetches its own state from the dedicated admin endpoint:
 * the place id is deliberately absent from GarageDetailResponse, because that DTO is also served to
 * garage owners through /api/garages/me.
 */
export default function GoogleListingSection({ garageId, garageName, canEdit }: GoogleListingSectionProps) {
  const { token } = useAuth();
  const [listing, setListing] = useState<GoogleListingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      setListing(await api.adminGarageGetGoogleListing(garageId, token));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the Google listing');
    } finally {
      setLoading(false);
    }
  }, [garageId, token]);

  useEffect(() => { void load(); }, [load]);

  const remove = async () => {
    if (!token) return;
    setRemoving(true);
    setError(null);
    try {
      await api.adminGarageUnlinkGoogleListing(garageId, token);
      setConfirmRemove(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove the Google link');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div style={{ marginTop: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <h4 style={{ margin: 0, color: '#374151', fontSize: '0.9rem', fontWeight: 600 }}>Google Listing</h4>
        {canEdit && !loading && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => setShowDialog(true)}>
              {listing?.placeId ? 'Change listing' : 'Search Google'}
            </button>
            {listing?.placeId && (
              <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => setConfirmRemove(true)}>
                Remove link
              </button>
            )}
          </div>
        )}
      </div>

      {loading && <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Loading…</div>}
      {error && <div className="admin-error">{error}</div>}

      {!loading && listing && !listing.placeId && (
        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
          Not linked. Customers see no Google rating on this garage’s card.
        </div>
      )}

      {!loading && listing?.placeId && (
        <>
          <div className="admin-detail-grid">
            <Field label="Google rating" value={listing.rating != null ? listing.rating.toFixed(1) : null} />
            <Field label="Google ratings" value={listing.ratingCount != null ? String(listing.ratingCount) : null} />
            <Field label="Last refreshed" value={relativeTime(listing.fetchedAt)} />
            <Field label="Link status" value={statusLabel(listing.linkStatus)} />
          </div>

          <div style={{ marginTop: '0.5rem', fontSize: '0.8125rem' }}>
            {listing.visibleToCustomers ? (
              <span style={{ color: '#16a34a' }}>Customers are seeing this rating.</span>
            ) : (
              <span style={{ color: '#d97706' }}>
                Customers are not seeing this rating
                {listing.fetchedAt == null
                  ? ' — it has not been fetched yet. The scheduled job will retry.'
                  : listing.stale
                    ? ' — it is older than the staleness limit.'
                    : listing.ratingCount === 0
                      ? ' — Google has no ratings for this listing.'
                      : '.'}
              </span>
            )}
          </div>

          {listing.linkStatus === 'NEEDS_REVERIFY' && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: '#d97706' }}>
              This garage’s name or address changed since the listing was linked. Re-verify it still points
              at the right business.
            </div>
          )}
          {listing.linkStatus === 'INVALID' && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: '#dc2626' }}>
              Google no longer recognises this listing. Link a new one.
            </div>
          )}

          <div style={{ marginTop: '0.5rem' }}>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(garageName)}&query_place_id=${encodeURIComponent(listing.placeId)}`}
              target="_blank"
              rel="noreferrer noopener"
              style={{ fontSize: '0.8125rem', color: '#2196f3' }}
            >
              View on Google Maps ↗
            </a>
          </div>
        </>
      )}

      {showDialog && (
        <LinkGoogleListingDialog
          garageId={garageId}
          garageName={garageName}
          onClose={() => setShowDialog(false)}
          onSaved={load}
        />
      )}

      {confirmRemove && (
        <div className="admin-modal-overlay" role="dialog" aria-modal="true">
          <div className="admin-modal">
            <div className="admin-modal-header"><h3>Remove Google link?</h3></div>
            <div className="admin-modal-body">
              <p style={{ margin: 0, fontSize: '0.875rem' }}>
                The Google rating will disappear from this garage’s card for every customer immediately.
                You can link a listing again later.
              </p>
            </div>
            <div className="admin-modal-footer">
              <button type="button" className="admin-btn" onClick={() => setConfirmRemove(false)} disabled={removing}>
                Cancel
              </button>
              <button type="button" className="admin-btn admin-btn-danger" onClick={remove} disabled={removing}>
                {removing ? 'Removing…' : 'Remove link'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{label}</div>
      <div style={{ fontSize: '0.875rem', color: '#1a1a2e' }}>{value ?? '—'}</div>
    </div>
  );
}

function statusLabel(status: GoogleListingResponse['linkStatus']): string | null {
  switch (status) {
    case 'LINKED': return 'Linked';
    case 'NEEDS_REVERIFY': return 'Needs re-verification';
    case 'INVALID': return 'Invalid';
    default: return null;
  }
}

/** Plain language, because an admin should never have to work out whether a timestamp is stale. */
function relativeTime(iso: string | null): string | null {
  if (!iso) return 'Never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const minutes = Math.round((Date.now() - then) / 60000);
  if (minutes < 2) return 'Just now';
  if (minutes < 60) return `${minutes} minutes ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}
